/**
 * Transient failures from Supabase or the gateway in front of it.
 *
 * A 502, 503, or 504, and the Cloudflare HTML or JSON page those statuses
 * often arrive as, is not a bug in the save the user just attempted. Callers
 * show one stable sentence instead of the raw body. Idempotent reads can try
 * the request once more before that sentence is needed.
 */

export const TRANSIENT_UPSTREAM_MESSAGE =
  'We hit a temporary problem. Please try again.';

export const TRANSIENT_UPSTREAM_DIGEST_PREFIX = 'TRANSIENT_UPSTREAM';

export type GatewayStatus = '502' | '503' | '504';

export interface TransientUpstream {
  status: GatewayStatus;
}

const GATEWAY_PHRASE =
  /\b(502|503|504)\s+(?:Bad Gateway|Service Temporarily Unavailable|Service Unavailable|Gateway Time-?out)\b/i;

const CLOUDFLARE_ERROR_CODE = /\berror code:\s*(502|503|504)\b/i;

const HTML_MARK = /<\/?(?:html|title|center|body|h1)\b/i;

/**
 * Thrown from the shared server-action wrapper so the browser receives a
 * sentence it can show. `digest` survives Next's production error masking,
 * which is how the client still knows this was a gateway failure.
 */
export class TransientUpstreamError extends Error {
  readonly status: GatewayStatus;
  readonly transientUpstream = true;
  digest: string;

  constructor(status: GatewayStatus, cause?: unknown) {
    super(TRANSIENT_UPSTREAM_MESSAGE);
    this.name = 'TransientUpstreamError';
    this.status = status;
    this.digest = `${TRANSIENT_UPSTREAM_DIGEST_PREFIX};${status}`;

    if (cause !== undefined) {
      this.cause = cause;
    }
  }
}

export function readTransientUpstream(
  error: unknown,
): TransientUpstream | null {
  return readTransient(error, 0);
}

/**
 * Text for a toast, form error, or inline alert.
 *
 * A gateway body, including one wrapped in a short prefix such as
 * "Failed to update supplier:", is replaced with the stable sentence. Any
 * other message is returned unchanged.
 */
export function sanitizeUserFacingText(text: string): string {
  if (!readTransientUpstream(text)) {
    return text;
  }

  const stripped = text
    .replace(/^\s*Error:\s*/i, '')
    .replace(/\{[\s\S]*\}/g, ' ')
    .replace(/<html\b[\s\S]*<\/html>/gi, ' ')
    .replace(GATEWAY_PHRASE, ' ')
    .replace(CLOUDFLARE_ERROR_CODE, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s:,.+-]+|[\s:,.+-]+$/g, '')
    .trim();

  if (!stripped || readTransientUpstream(stripped)) {
    return TRANSIENT_UPSTREAM_MESSAGE;
  }

  return `${stripped.replace(/\.+$/, '')}. ${TRANSIENT_UPSTREAM_MESSAGE}`;
}

export function userFacingErrorMessage(error: unknown): string {
  if (readTransientUpstream(error)) {
    const own = ownMessage(error);

    if (own) {
      return sanitizeUserFacingText(own);
    }

    return TRANSIENT_UPSTREAM_MESSAGE;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (isRecord(error) && typeof error.message === 'string') {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
}

const RETRY_DELAY_MS = 150;

/**
 * One extra attempt for an idempotent read that came back 502, 503, or 504.
 *
 * Writes are not retried: the first request may already have been applied.
 * A second failure is returned as-is so the caller can show the friendly
 * message and the report can still be recorded.
 */
export function fetchWithTransientReadRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  return runTransientReadRetry(input, init, (nextInput, nextInit) =>
    fetch(nextInput, nextInit),
  );
}

export function createTransientReadFetch(
  baseFetch: typeof fetch,
  options?: {
    delayMs?: number;
    sleep?: (ms: number) => Promise<void>;
  },
): typeof fetch {
  const delayMs = options?.delayMs ?? RETRY_DELAY_MS;
  const sleep = options?.sleep ?? delay;

  return (input, init) =>
    runTransientReadRetry(input, init, baseFetch, delayMs, sleep);
}

async function runTransientReadRetry(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  baseFetch: typeof fetch,
  delayMs = RETRY_DELAY_MS,
  sleep: (ms: number) => Promise<void> = delay,
): Promise<Response> {
  const response = await baseFetch(input, init);

  if (!shouldRetryRead(input, init, response.status)) {
    return response;
  }

  await releaseResponse(response);
  await sleep(delayMs);

  return baseFetch(input, init);
}

export function shouldRetryRead(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  status: number,
): boolean {
  if (init?.signal?.aborted) {
    return false;
  }

  if (!isGatewayStatus(status)) {
    return false;
  }

  return isIdempotentRead(requestMethod(input, init));
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) {
    return init.method.toUpperCase();
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.method.toUpperCase();
  }

  return 'GET';
}

function isIdempotentRead(method: string) {
  return method === 'GET' || method === 'HEAD';
}

function isGatewayStatus(status: number) {
  return status === 502 || status === 503 || status === 504;
}

async function releaseResponse(response: Response) {
  try {
    await response.body?.cancel();
  } catch {
    /* The body may already be closed. The retry does not need it. */
  }
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function readTransient(
  error: unknown,
  depth: number,
): TransientUpstream | null {
  if (depth > 4 || error == null) {
    return null;
  }

  if (typeof error === 'string') {
    return transientFromText(error);
  }

  if (typeof error !== 'object') {
    return null;
  }

  const record = error as Record<string, unknown>;
  const digestStatus = statusFromDigest(record.digest);

  if (digestStatus) {
    return { status: digestStatus };
  }

  if (record.transientUpstream === true) {
    const flagged = normalizeGatewayStatus(record.status);

    if (flagged) {
      return { status: flagged };
    }
  }

  const status = normalizeGatewayStatus(record.status ?? record.statusCode);

  if (status) {
    return { status };
  }

  const codeStatus = normalizeGatewayStatus(record.code);

  if (codeStatus && gatewayTextAround(record)) {
    return { status: codeStatus };
  }

  for (const key of [
    'message',
    'error',
    'error_description',
    'details',
    'hint',
    'value',
  ]) {
    const nested = readTransient(record[key], depth + 1);

    if (nested) {
      return nested;
    }
  }

  if ('cause' in record) {
    return readTransient(record.cause, depth + 1);
  }

  return null;
}

function transientFromText(text: string): TransientUpstream | null {
  const trimmed = text.trim().replace(/^Error:\s*/, '');

  if (!trimmed) {
    return null;
  }

  const parsed = parseJsonContainer(trimmed);

  if (parsed !== null) {
    const fromJson = readTransient(parsed, 1);

    if (fromJson) {
      return fromJson;
    }
  }

  return gatewayPhrase(trimmed);
}

function gatewayPhrase(text: string): TransientUpstream | null {
  const phrase = text.match(GATEWAY_PHRASE);

  if (phrase) {
    const status = phrase[1] as GatewayStatus;
    const strong =
      /cloudflare/i.test(text) ||
      HTML_MARK.test(text) ||
      text.trim().length <= 240;

    if (strong) {
      return { status };
    }
  }

  const code = text.match(CLOUDFLARE_ERROR_CODE);

  if (code && /cloudflare/i.test(text)) {
    return { status: code[1] as GatewayStatus };
  }

  return null;
}

function gatewayTextAround(record: Record<string, unknown>) {
  const message = record.message ?? record.error ?? record.error_description;

  return typeof message === 'string' && gatewayPhrase(message) !== null;
}

function statusFromDigest(digest: unknown): GatewayStatus | null {
  if (typeof digest !== 'string') {
    return null;
  }

  const match = digest.match(
    new RegExp(`^${TRANSIENT_UPSTREAM_DIGEST_PREFIX};(502|503|504)$`),
  );

  return (match?.[1] as GatewayStatus | undefined) ?? null;
}

export function normalizeGatewayStatus(value: unknown): GatewayStatus | null {
  if (typeof value === 'number' && isGatewayStatus(value)) {
    return String(value) as GatewayStatus;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  return trimmed === '502' || trimmed === '503' || trimmed === '504'
    ? trimmed
    : null;
}

function ownMessage(error: unknown): string | null {
  if (typeof error === 'string') {
    return error;
  }

  if (!isRecord(error)) {
    return null;
  }

  if (typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return null;
}

function parseJsonContainer(text: string): unknown | null {
  if (!text.startsWith('{') && !text.startsWith('[')) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
